// frontend/src/components/Auth/Login.js
import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, writeBatch, arrayUnion, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getProjectMemberPath, getUserProjectPath } from '../../utils/collectionPaths';

// MUI Imports
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper'; // To give a card-like appearance
// Example Icon
import raysImageUrl from '../../assets/images/rays.png'
import horizontalLightImageUrl from '../../assets/images/horizontal-light.svg'

// frontend/src/components/Auth/Login.js
// ... (imports remain the same)

const CLI_AUTH_RETURN_URL_KEY = 'segmently.cliAuth.returnUrl';
const normalizeEmail = (value) => (value || '').trim().toLowerCase();

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState(''); // For password reset success
  const navigate = useNavigate();
  const location = useLocation();
  const cliAuthReturnUrl = sessionStorage.getItem(CLI_AUTH_RETURN_URL_KEY);
  const returnUrl = location.state?.returnUrl
    || (cliAuthReturnUrl?.startsWith('/cli-auth?') ? cliAuthReturnUrl : null)
    || '/all-projects';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfoMessage(''); // Clear info message on new submit attempt
    try {
      await signInWithEmailAndPassword(auth, email, password);

      // Check for pending invitation in sessionStorage
      const pendingInvitation = sessionStorage.getItem('pendingInvitation');
      if (pendingInvitation) {
        try {
          const invitation = JSON.parse(pendingInvitation);
          const signedInEmail = normalizeEmail(auth.currentUser?.email || email);
          const recipientEmail = normalizeEmail(invitation.recipientEmail);

          if (!recipientEmail || signedInEmail !== recipientEmail) {
            setError(
              `This invitation is for ${invitation.recipientEmail || 'another email address'}. ` +
              `Please sign in with that account to accept it.`
            );
            return;
          }

          // Get project name first
          const projectRef = doc(db, 'projects', invitation.projectId);
          const projectSnap = await getDoc(projectRef);
          const projectName = projectSnap.exists() ? projectSnap.data().projectName : 'Unknown Project';

          // Prepare member data
          const memberData = {
            role: invitation.roleToAssign,
            email: email,
            joinedAt: new Date()
          };

          // Prepare user project data
          const userProjectData = {
            name: projectName,
            role: invitation.roleToAssign,
            addedAt: new Date(),
            updatedAt: new Date()
          };

          // Step 1: Batch write for member, userProject, and invitation status
          // memberIds update is separate (Step 2) — Firestore security rules
          // evaluate against pre-batch state, so isProjectMember() would fail
          const batch = writeBatch(db);

          const memberRef = doc(db, getProjectMemberPath(invitation.projectId, auth.currentUser.uid));
          const userProjectRef = doc(db, getUserProjectPath(auth.currentUser.uid, invitation.projectId));
          const invitationRef = doc(db, 'invitations', invitation.id);

          batch.set(memberRef, memberData);
          batch.set(userProjectRef, userProjectData);
          batch.update(invitationRef, {
            status: 'accepted',
            acceptedAt: new Date(),
            acceptedBy: auth.currentUser.uid
          });

          await batch.commit();

          // Step 2: Add to memberIds after member doc exists
          await updateDoc(projectRef, {
            memberIds: arrayUnion(auth.currentUser.uid)
          });

          // Clear the pending invitation from sessionStorage
          sessionStorage.removeItem('pendingInvitation');

          // Show success message and redirect to dashboard
          navigate('/all-projects');
          return;
        } catch (invitationError) {
          console.error('Error processing invitation:', invitationError);
          // Continue with normal login flow even if invitation processing fails
        }
      }

      if (returnUrl.startsWith('/cli-auth?')) {
        sessionStorage.removeItem(CLI_AUTH_RETURN_URL_KEY);
      }
      navigate(returnUrl);
    } catch (err) {
      let errorMessage = "Failed to sign in.";
      // console.error("Firebase login error:", err, "Code:", err.code); // For debugging

      // Check for error codes that might indicate user not found
      // 'auth/user-not-found' is older, 'auth/invalid-credential' is newer and more general for bad email/password combo.
      if (err.code === 'auth/user-not-found') {
        // If this specific code is received, redirect to sign up with email pre-filled
        navigate('/signup', { state: { email: email, message: "No account found with this email. Please sign up.", returnUrl } });
        return; // Important to return to prevent setting local error message
      } else if (err.code === 'auth/invalid-credential') {
        // This is a common error for "wrong password" OR "user not found"
        // For a better UX, you might not want to redirect immediately on this,
        // as it could be just a wrong password for an existing user.
        // However, if you want to be more aggressive with the redirect:
        // For now, let's treat it as a general login failure for this example.
        // If you wanted to try redirecting here too, you'd need a way to confirm
        // it's specifically "user not found" which Firebase often obscures.
        errorMessage = "Invalid email or password. Please try again.";
      } else if (err.code === 'auth/wrong-password') { // Older code, less likely with newer SDKs for this case
        errorMessage = "Invalid email or password.";
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = "The email address format is not valid.";
      } else {
        errorMessage = "An unexpected error occurred. Please try again.";
      }
      setError(errorMessage);
    }
  };



  // ... (return statement with MUI components remains the same) ...
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100svh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container component="main" maxWidth="xs">
        <img
          src={horizontalLightImageUrl}
          alt='horizontal light image'
          style={{
            height: 'auto',
            width: '100%',
            position: 'absolute',
            top: '0',
            left: '0',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            userSelect: 'none',
            filter: 'blur(10px)'
          }}
        />
        <img
          src={horizontalLightImageUrl}
          alt='horizontal light image'
          style={{
            height: 'auto',
            width: '100%',
            position: 'absolute',
            bottom: '0',
            left: '0',
            transform: 'translateY(50%)',
            pointerEvents: 'none',
            userSelect: 'none',
            objectFit: 'cover',
            filter: 'blur(10px)'
          }}
        />
        <Paper
          elevation={1} sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'none',
            boxShadow: 'none',
            padding: (theme) => theme.spacing(3, 3, 3)
          }}
        >
          <Box sx={{
            height: '150px',
            width: 'auto',
          }}>
            <img src={raysImageUrl} alt='rays image' style={{ height: '100%', width: 'auto', transform: 'scale(1.8)' }} />
          </Box>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          {error && (
            <Typography color="error" sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
              {error}
            </Typography>
          )}
          {infoMessage && ( // Display info message for password reset
            <Typography color="info.main" sx={{ mt: 2, width: '100%', textAlign: 'center', backgroundColor: 'info.light', padding: 1, borderRadius: 1 }}>
              {infoMessage}
            </Typography>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid>
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot password?
                </Link>
              </Grid>
              { }
              {(process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging') &&
                <Grid>
                  {/*                  <Link component={RouterLink} to="/signup" variant="body2">
                      Create new account
                    </Link> */}
                </Grid>
              }
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
export default Login;
